/**
 * Human override of a Targeting Strategist reject (the reject-paused case).
 *
 * The strategist advises; the human decides. Two ways forward:
 *  - Pick a candidate the strategist already analyzed → its retained SERP is
 *    projected and the pipeline advances to the brief with no re-run.
 *  - Point it at a new keyword → the strategist re-runs in forced-proceed mode.
 *
 * Both append an immutable editorial-history record (no rationale required yet).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { blogRunsService } from '../../../services/blogRunsService';
import { jobsService } from '../../../services/jobsService';
import { useArticleContext } from '../ArticleContext';
import type { StrategyCandidate } from '../types';

type OverridePayload = { mode: 'pick' | 'new'; keyword: string; angle?: string };

export function StrategistOverridePanel({
  candidates,
  verdict,
  chosenKeyword,
}: {
  candidates: StrategyCandidate[];
  verdict?: 'proceed' | 'reject';
  chosenKeyword?: string;
}) {
  const { run, refreshRun, refreshDocument, refreshArticles } = useArticleContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [angle, setAngle] = useState('');

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const isReject = verdict === 'reject';

  const runOverride = useCallback(async (payload: OverridePayload) => {
    if (!run?.run_id) return;
    setBusy(true);
    setError(null);
    try {
      const { job_id } = await blogRunsService.overrideTargeting(run.run_id, payload);
      await refreshRun();
      // Poll the resumed pipeline, refreshing stage state as it advances
      // (mirrors RunControls). The panel unmounts once the run leaves the
      // rejected-pause state, but the loop keeps refreshing the context.
      const maxPolls = 180;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const job = await jobsService.get(job_id);
          await refreshRun();
          await refreshDocument();
          if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
            await refreshArticles();
            break;
          }
        } catch {
          /* transient poll error — keep going */
        }
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : 'Override failed');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [run?.run_id, refreshRun, refreshDocument, refreshArticles]);

  if (!verdict) return null;

  // Every candidate the strategist considered (minus the chosen one) is
  // one-click: retained ones proceed instantly (mode=pick), the rest are
  // re-analyzed (mode=new).
  const alternatives = candidates.filter((c) => c.keyword && c.keyword !== chosenKeyword);

  return (
    <div className="border border-indigo-200 bg-indigo-50 rounded p-3 space-y-3">
      <div>
        <div className="text-xs font-semibold text-indigo-800">
          {isReject ? 'Override — proceed anyway' : 'Redirect the target'}
        </div>
        <p className="text-xs text-indigo-700 mt-0.5">
          {isReject
            ? 'The strategist advises; you decide. Proceed with a candidate it already analyzed, or point it at a new keyword to analyze.'
            : `The strategist chose “${chosenKeyword || 'this target'}.” Prefer a different one? Pick another candidate it analyzed, or point it at a new keyword — this re-runs the pipeline from targeting.`}
        </p>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      )}

      {alternatives.length > 0 && (
        <div>
          <div className="text-xs font-medium text-indigo-800 mb-1">Candidates it considered — pick one</div>
          <div className="space-y-1">
            {alternatives.map((c, i) => (
              <button
                key={i}
                type="button"
                disabled={busy}
                onClick={() => runOverride({ mode: c.has_serp ? 'pick' : 'new', keyword: c.keyword })}
                title={c.has_serp ? 'Retained analysis — proceeds straight to the brief' : 'Re-analyzes this keyword, then proceeds'}
                className="w-full text-left text-xs px-2 py-1.5 rounded border border-indigo-200 bg-white hover:bg-indigo-100 disabled:opacity-50 flex items-center justify-between gap-2"
              >
                <span className="text-gray-800 truncate">{c.keyword}</span>
                <span className="text-gray-500 whitespace-nowrap flex items-center gap-1.5">
                  {c.seo_score != null && <>SEO {c.seo_score} </>}
                  {c.aeo_score != null && <>AEO {c.aeo_score}</>}
                  <span className={`text-[10px] px-1 py-0.5 rounded ${c.has_serp ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {c.has_serp ? 'ready' : 'analyze'}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-medium text-indigo-800 mb-1">Or a keyword not listed</div>
        <div className="flex gap-2">
          <input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            disabled={busy}
            placeholder="e.g. business english for engineers"
            className="flex-1 text-xs px-2 py-1.5 rounded border border-indigo-200 bg-white"
          />
          <button
            type="button"
            disabled={busy || !newKeyword.trim()}
            onClick={() => runOverride({ mode: 'new', keyword: newKeyword.trim(), angle: angle.trim() || undefined })}
            className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? 'Working…' : 'Analyze & proceed'}
          </button>
        </div>
        <input
          value={angle}
          onChange={(e) => setAngle(e.target.value)}
          disabled={busy}
          placeholder="Optional angle / emphasis"
          className="mt-1 w-full text-xs px-2 py-1.5 rounded border border-indigo-200 bg-white"
        />
      </div>

      {busy && (
        <div className="text-xs text-indigo-700">Running the pipeline… watch progress in the stage sidebar.</div>
      )}
    </div>
  );
}
